import { useCallback, useContext, useMemo } from 'react'
import {
  PrefixedUserDocumentCategoryIdentifier,
  UserDocumentCategoryIdentifier,
} from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import { Document, DocumentFile } from '../../types'
import { AdminYearEndReviewContext } from '../context'
import { DocumentRow, DocumentRowProps } from '../document-row'
import { useTherapistFlag, CaseStyle } from '@/utils/launchdarklyHelpers'

const { formW3, form1099nec } = UserDocumentCategoryIdentifier

const WAITING = 'Waiting on documents from payroll provider'

export type HeardPayrollRowProps = {
  categoryCode: string
  stepDocuments: Document[] | undefined
  getFileIfExists: (doc: Document) => DocumentFile | undefined
  onRequestReUpload: (document: Document) => void
}
const HeardPayrollRow = ({
  categoryCode,
  stepDocuments,
  getFileIfExists,
  onRequestReUpload,
}: HeardPayrollRowProps) => {
  const { taxYear } = useContext(AdminYearEndReviewContext)
  const replacementText = useTherapistFlag(CaseStyle.lowerCase, false)

  const INTEGRATION_ERROR = `Error downloading documents from payroll provider. Request ${replacementText} reupload.`

  const payrollJournal = useMemo(
    () => PrefixedUserDocumentCategoryIdentifier.payrollJournal(taxYear),
    [taxYear]
  )

  const contractorPaymentsReport = useMemo(
    () =>
      PrefixedUserDocumentCategoryIdentifier.contractorPaymentsReport(taxYear),
    [taxYear]
  )

  const getDescription = useCallback(
    (categoryCode: string) => {
      switch (categoryCode) {
        case formW3:
          return 'Form W3'
        case payrollJournal:
          return 'Payroll Journal'
        case form1099nec:
          return '1099-NEC'
        case contractorPaymentsReport:
          return 'Contractor Payment Report'
        default:
          return categoryCode
      }
    },
    [payrollJournal, contractorPaymentsReport]
  )

  const documentRowProps = useMemo<
    DocumentRowProps<Document> | undefined
  >(() => {
    const document = stepDocuments?.find(
      (doc) => doc.provider === 'Heard' && categoryCode === doc.categoryCode
    )
    const file = document ? getFileIfExists(document) : undefined

    return {
      description: `Heard - ${getDescription(categoryCode)}`,
      document: document ?? { categoryCode, provider: 'Heard' },
      file,
      testId: `Heard_${categoryCode}`,
      onRequestReUpload,
      children: file
        ? undefined
        : document?.integrationError
          ? INTEGRATION_ERROR
          : WAITING,
    }
  }, [
    categoryCode,
    stepDocuments,
    getFileIfExists,
    getDescription,
    onRequestReUpload,
    INTEGRATION_ERROR,
  ])

  if (!documentRowProps) {
    return null
  }

  return <DocumentRow {...documentRowProps} />
}

export default HeardPayrollRow
